import {
  forwardRef,
  ReactNode,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {
  InputModeOptions,
  KeyboardTypeOptions,
  TextInput,
  TextStyle,
} from 'react-native';

import { twMerge } from 'tailwind-merge';

import { useControllableState } from '@rn-primitives/hooks';
import { KeyboardOverlay } from './KeyboardOverlay/KeyboardOverlay';
import { mapFontWeights } from './helper';

interface TextFieldProps {
  style?: TextStyle;
  className?: string;
  value?: string;
  onValueChanged?: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  keyboardType?: KeyboardTypeOptions;
  isFocused?: boolean;
  maxLength?: number;
  placeholder: string;
  readOnly?: boolean;
  fontSize?: number;
  fontWeight?: TextStyle['fontWeight'];
  lineHeigth?: number;
  inputMode?: InputModeOptions;
  keyboardOverlay?: ReactNode;
}

export interface TextFieldRef {
  focus: () => void;
  blur: () => void;
}

const TextField = forwardRef<TextFieldRef, TextFieldProps>(
  (
    {
      style,
      className,
      value,
      keyboardType = 'default',
      onValueChanged,
      onFocus,
      onBlur,
      isFocused = false,
      maxLength,
      placeholder,
      readOnly = false,
      fontSize = 24,
      fontWeight = 600,
      inputMode,
      keyboardOverlay,
      lineHeigth,
    },
    ref
  ) => {
    const inputAccessoryViewID = useId();
    const [focused, setIsFocused] = useState(false);
    const inputRef = useRef<TextInput>(null);

    const [internalValue, handleValueChanged] = useControllableState({
      prop: value,
      onChange: onValueChanged,
      defaultProp: '',
    });

    useImperativeHandle(ref, () => ({
      focus: () => {
        inputRef.current?.focus();
      },
      blur: () => {
        inputRef.current?.blur();
      },
    }));

    // TODO: rework this
    let customStyle: TextStyle = {
      textAlignVertical: 'center',
      fontFamily: 'Bozon',
      fontSize: fontSize,
      fontWeight: fontWeight,
      lineHeight: lineHeigth,
      ...style,
    };

    customStyle = mapFontWeights(customStyle);

    useEffect(() => {
      if (isFocused) {
        inputRef?.current?.focus();
      }
    }, [isFocused]);

    return (
      <>
        <TextInput
          inputMode={inputMode}
          style={customStyle}
          placeholder={placeholder}
          placeholderTextColor={'#A4A7C1'}
          cursorColor={'#626AFF'}
          ref={inputRef}
          keyboardType={keyboardType}
          className={twMerge('focus:outline-none text-white', className)}
          value={internalValue}
          onChangeText={(text) => handleValueChanged?.(text)}
          onFocus={() => {
            setIsFocused(true);
            onFocus?.();
          }}
          onBlur={() => {
            setIsFocused(false);
            onBlur?.();
          }}
          autoFocus={isFocused}
          maxLength={maxLength}
          readOnly={readOnly}
          inputAccessoryViewID={
            keyboardOverlay != null ? inputAccessoryViewID : undefined
          }
        />
        <KeyboardOverlay
          inputAccessoryViewID={inputAccessoryViewID}
          inputFocused={focused}
        >
          {keyboardOverlay}
        </KeyboardOverlay>
      </>
    );
  }
);

export default TextField;
